import { floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { DeliveryPackageLineView } from "src/api/generated/erp/db/types/tables/deliveryPackageLineView.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid.tsx";
import { RefreshableElementProps, RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { useClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/useClientSideDataGridModel.tsx";
import { DeliveryPackageApi } from "src/api/generated/erp/delivery/api/deliveryPackageApi.ts";
import { DecreaseDeliveryPackageLineQuantityForm } from "src/components/views/erp/delivery/deliveryPackage/picking/DecreaseDeliveryPackageLineQuantityForm.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { DeliveryPackageView } from "src/api/generated/erp/db/types/tables/deliveryPackageView.ts";
import { DeliveryPackageSource } from "src/api/generated/erp/delivery/model/deliveryPackageSource.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface DeliveryPackageLinesDataGridProps extends RefreshableElementProps {
	deliveryPackage: Pick<DeliveryPackageView, "deliveryPackageId" | "state" | "deliveryState">;
	deliveryPackageSource?: DeliveryPackageSource;
	refreshRef?: RefreshableElementRef;
	pickableLinesViewRefreshRef?: RefreshableElementRef;
	/** Reads the lines through the cargo driver API, which accepts only packages of the driver's own transports. */
	cargoDriverView?: boolean;
}

export const DeliveryPackageLinesDataGrid = ({
	deliveryPackage,
	deliveryPackageSource,
	pickableLinesViewRefreshRef,
	refreshRef,
	cargoDriverView = false,
}: DeliveryPackageLinesDataGridProps) => {
	const { dataGridProps, onlySelectedRow, refreshData } = useClientSideDataGridModel({
		customFunctionUiActionView: "DELIVERY_PACKAGE_LINES",
		fetchData: () =>
			cargoDriverView ?
				DeliveryPackageApi.getPackageLinesForCargoDriver({
					deliveryPackageId: deliveryPackage.deliveryPackageId,
				})
			:	DeliveryPackageApi.getPackageLines({
					deliveryPackageId: deliveryPackage.deliveryPackageId,
					deliveryPackageSource: deliveryPackageSource,
				}),
		initialParams: {},
		getRowId: (row) => row.deliveryPackageLineId,
		gridId: "DF172DA1E15F6901",
		refreshRef: refreshRef,
	});

	const { openDialog } = useGenericDialog();

	const hasPermission = useHasPermission();
	const editDeliveryPackagesPermission =
		!cargoDriverView && hasPermission(Permissions.Delivery.DeliveryPackages.MANAGE);
	const packageSourceHeader = getPackageSourceHeader(deliveryPackageSource);

	return (
		<ControlledAsyncFormCrudDataGrid<DeliveryPackageLineView>
			columns={[
				integerColumn({
					field: "packageSourceId",
					headerName: packageSourceHeader,
					width: 120,
					mobile: "cell",
				}),
				integerColumn({
					field: "sourceLineNumber",
					headerName: i18n.t("line_number"),
					mobile: "header",
				}),
				textColumn({
					field: "configurationOrPartDescription",
					headerName: i18n.t("part"),
					width: 300,
					mobile: "header",
				}),
				floatColumn({
					field: "quantityInBaseUnit",
					headerName: i18n.t("picked_quantity"),
					width: 120,
					mobile: "cell",
				}),
				textColumn({
					field: "partUnit",
					headerName: i18n.t("unit"),
					mobile: "cell",
				}),
				textColumn({
					field: "sourceMark",
					headerName: i18n.t("mark"),
					mobile: "cell",
				}),
			]}
			listViewOnMobile
			actionBarComponents={
				<>
					{editDeliveryPackagesPermission && (
						<AsyncButton
							icon={faTrash}
							disabled={() => {
								if (!onlySelectedRow) return i18n.t("select_line");
								if (deliveryPackage.deliveryState === "DELIVERED")
									return i18n.t("package_is_delivered");
								if (deliveryPackage.state === "COMPLETED") return i18n.t("package_is_picked");
								if (deliveryPackage.state === "LOADED") return i18n.t("package_is_loaded");
								return false;
							}}
							onClick={async () => {
								if (onlySelectedRow) {
									await openDecreaseQuantityDialog(onlySelectedRow);
								}
							}}
						/>
					)}
				</>
			}
			agentContext={{
				key: defineAgentUiContextKey("erp.delivery.deliveryPackageLines.grid"),
				data: { deliveryPackageId: deliveryPackage.deliveryPackageId },
				rowFields: ["deliveryPackageLineId", "deliveryPackageId", "sourceType", "sourceId"],
				selectedRowPathSegment: (row) =>
					`${i18n.t("package_lines")} ${row.deliveryPackageLineId}`,
			}}
			{...dataGridProps}
		/>
	);

	async function openDecreaseQuantityDialog(row: DeliveryPackageLineView) {
		openDialog(({ closeDialog }) => ({
			title: i18n.t("delete"),
			size: "sm",
			agentContext: {
				key: defineAgentUiContextKey("erp.delivery.deliveryPackageLineDecrease.dialog"),
				pathSegments: [
					`${i18n.t("package_lines")} ${row.deliveryPackageLineId}`,
					i18n.t("delete"),
				],
				data: {
					deliveryPackageId: deliveryPackage.deliveryPackageId,
					deliveryPackageLineId: row.deliveryPackageLineId,
				},
			},
			content: (
				<DecreaseDeliveryPackageLineQuantityForm
					deliveryPackageLine={row}
					onCompleted={async () => {
						await closeDialog();
						await Promise.all([pickableLinesViewRefreshRef?.refresh(), refreshData()]);
					}}
				/>
			),
		}));
	}
};

function getPackageSourceHeader(deliveryPackageSource: DeliveryPackageSource | undefined): string {
	switch (deliveryPackageSource?.type) {
		case "customerOrder":
			return i18n.t("customer_order");
		case "project":
			return i18n.t("project");
		default:
			return `${i18n.t("customer_order")} / ${i18n.t("project")}`;
	}
}
